import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, MenuItem, Pagination, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

import { createGlobalIngredient, deactivateGlobalIngredient, getGlobalIngredientCatalog, getGlobalIngredientCategories, updateGlobalIngredient } from "../api/consultantApi";
import { INGREDIENT_SECTORS } from "../../stock/baseDatos/insumos/utils/ingredientFormatters";
import GlobalIngredientFormDialog from "../components/GlobalIngredientFormDialog";
import GlobalIngredientsTable from "../components/GlobalIngredientsTable";

const PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 500;
const filterStyle = { height: 38, borderRadius: "33px", bgcolor: "neutral.100", color: "primary.900", fontSize: 13, fontWeight: 700, boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)", "& .MuiOutlinedInput-notchedOutline": { border: "none" } };

export default function GlobalIngredientsPage() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [sector, setSector] = useState("");
  const [status, setStatus] = useState("true");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [formItem, setFormItem] = useState(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [operationError, setOperationError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);
  const [blockingLicenses, setBlockingLicenses] = useState([]);

  useEffect(() => {
    const timeout = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, SEARCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    getGlobalIngredientCategories({ signal: controller.signal }).then((result) => setCategories(Array.isArray(result) ? result : [])).catch((requestError) => {
      if (requestError?.name !== "AbortError") setError(requestError?.message || "No se pudieron cargar las categorías.");
    });
    return () => controller.abort();
  }, [reloadKey]);

  const loadCatalog = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getGlobalIngredientCatalog({ page, limit: PAGE_SIZE, name: search || undefined, category: category || undefined, sector: sector || undefined, isActive: status === "" ? undefined : status, signal: controller.signal })
      .then((result) => {
        const data = Array.isArray(result?.data) ? result.data : [];
        setRows(data);
        setTotalItems(Number(result?.meta?.totalItems ?? data.length));
        setTotalPages(Number(result?.meta?.totalPages ?? (data.length ? 1 : 0)));
      })
      .catch((requestError) => {
        if (requestError?.name === "AbortError") return;
        setRows([]); setTotalItems(0); setTotalPages(0);
        setError(requestError?.message || "No se pudo cargar la lista global de insumos.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, search, category, sector, status]);

  useEffect(() => loadCatalog(), [loadCatalog, reloadKey]);
  const summary = useMemo(() => !totalItems ? "Sin insumos para mostrar" : `Mostrando ${rows.length} de ${totalItems} insumos`, [rows.length, totalItems]);
  const refresh = () => setReloadKey((value) => value + 1);
  const openCreate = () => { setFormItem(undefined); setOperationError(""); setFormOpen(true); };
  const openEdit = (item) => { setFormItem(item); setOperationError(""); setFormOpen(true); };

  const save = async (payload) => {
    setSaving(true); setOperationError("");
    try {
      if (formItem) await updateGlobalIngredient(formItem.id, payload);
      else await createGlobalIngredient(payload);
      setSuccess(formItem ? "El insumo se actualizó en el catálogo y sus licencias vinculadas." : "El insumo se creó y ya está disponible para las licencias.");
      setFormOpen(false); setPage(1); refresh();
    } catch (requestError) {
      setOperationError(requestError?.message || "No se pudo guardar el insumo.");
    } finally { setSaving(false); }
  };

  const confirmDeactivate = async () => {
    setSaving(true); setOperationError(""); setBlockingLicenses([]);
    try {
      await deactivateGlobalIngredient(deleteItem.id);
      setSuccess(`${deleteItem.name} se dio de baja correctamente.`);
      setDeleteItem(null); setPage(1); refresh();
    } catch (requestError) {
      const blocked = requestError?.data?.code === "INGREDIENT_CATALOG_ITEM_IN_USE";
      if (blocked) setBlockingLicenses(requestError.data.blockingLicenses || []);
      setOperationError(requestError?.message || "No se pudo dar de baja el insumo.");
    } finally { setSaving(false); }
  };

  return (
    <Stack spacing={2.5} sx={{ width: "100%", p: 2, boxSizing: "border-box" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5}>
        <Box>
          <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 900, color: "primary.900" }}>Administrar catálogo global</Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "neutral.700", mt: 0.5 }}>Creá y mantené la base compartida. Esta pantalla no agrega insumos a una licencia.</Typography>
        </Box>
        <Button
          type="button"
          onClick={openCreate}
          sx={{
            minWidth: { xs: "100%", md: 124 },
            height: 38,
            px: 2.4,
            borderRadius: "33px",
            bgcolor: "primary.500",
            color: "logoblanco",
            textTransform: "none",
            fontSize: 12,
            fontWeight: 800,
            whiteSpace: "nowrap",
            boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
            "&:hover": { bgcolor: "primary.600" },
          }}
        >
          Crear insumo
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "30px", bgcolor: "neutral.300", boxShadow: "inset 0 0 5px rgba(117,117,138,.3)" }}>
        <Stack spacing={2}>
          {success ? <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert> : null}
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.2} sx={{ width: "100%" }}>
            <Select value={sector} onChange={(event) => { setSector(event.target.value); setPage(1); }} displayEmpty size="small" inputProps={{ "aria-label": "Filtrar por sector" }} renderValue={(value) => value || "Todos los sectores"} sx={{ ...filterStyle, flex: 1, minWidth: 190 }}>
              <MenuItem value="">Todos los sectores</MenuItem>{INGREDIENT_SECTORS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </Select>
            <Select value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }} displayEmpty size="small" inputProps={{ "aria-label": "Filtrar por categoría" }} renderValue={(value) => value || "Todas las categorías"} sx={{ ...filterStyle, flex: 1.2, minWidth: 220 }}>
              <MenuItem value="">Todas las categorías</MenuItem>{categories.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </Select>
            <Select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} displayEmpty size="small" inputProps={{ "aria-label": "Filtrar por estado" }} renderValue={(value) => value === "true" ? "Activos" : value === "false" ? "Inactivos" : "Todos los estados"} sx={{ ...filterStyle, flex: 0.9, minWidth: 170 }}>
              <MenuItem value="">Todos los estados</MenuItem><MenuItem value="true">Activos</MenuItem><MenuItem value="false">Inactivos</MenuItem>
            </Select>
            <TextField value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Buscar insumo" inputProps={{ "aria-label": "Buscar insumo" }} size="small" sx={{ flex: 2, minWidth: 260, "& .MuiOutlinedInput-root": { height: 38, borderRadius: "33px", bgcolor: "neutral.50", color: "primary.900", fontSize: 13, fontWeight: 600, boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)", "& fieldset": { border: "none" } } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "primary.900" }} /></InputAdornment> }} />
          </Stack>
          {error ? <Alert severity="error" action={<Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={refresh}>Reintentar</Button>}>{error}</Alert> : null}
          <GlobalIngredientsTable rows={rows} loading={loading} onEdit={openEdit} onDeactivate={(item) => { setDeleteItem(item); setBlockingLicenses([]); setOperationError(""); }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>{summary}</Typography>
            <Pagination page={page} count={Math.max(totalPages, 1)} onChange={(_, value) => setPage(value)} color="primary" size="small" siblingCount={0} boundaryCount={1} disabled={loading || totalPages <= 1} />
          </Stack>
        </Stack>
      </Paper>

      <GlobalIngredientFormDialog open={formOpen} item={formItem} categories={categories} saving={saving} apiError={operationError} onClose={() => setFormOpen(false)} onSubmit={save} />
      <Dialog open={Boolean(deleteItem)} onClose={saving ? undefined : () => setDeleteItem(null)} fullWidth maxWidth="sm" aria-labelledby="deactivate-global-ingredient-title">
        <DialogTitle id="deactivate-global-ingredient-title" sx={{ fontWeight: 900, color: "primary.900" }}>Dar de baja insumo global</DialogTitle>
        <DialogContent>
          <DialogContentText>¿Querés dar de baja “{deleteItem?.name}”? Dejará de ofrecerse para nuevas activaciones.</DialogContentText>
          {operationError ? <Alert severity={blockingLicenses.length ? "warning" : "error"} sx={{ mt: 2 }}>{operationError}</Alert> : null}
          {blockingLicenses.length ? <Box sx={{ mt: 2 }}><Typography sx={{ fontWeight: 800, color: "primary.900" }}>Licencias que todavía lo usan:</Typography><Box component="ul" sx={{ mt: 1, mb: 0 }}>{blockingLicenses.map((license) => <li key={license.id}>{license.name}</li>)}</Box></Box> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={() => setDeleteItem(null)} disabled={saving}>Cancelar</Button>
          {!blockingLicenses.length ? <Button variant="contained" color="error" onClick={confirmDeactivate} disabled={saving}>{saving ? "Procesando…" : "Dar de baja"}</Button> : null}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
